// Minimal hand-rolled HTTP client for the Jotty Go API.
//
// Replaced wholesale by an OpenAPI-generated client in ENG-898 (M4). For now
// this gives M2 just enough surface to ship the auth + onboarding flow.

export interface ApiClientConfig {
  /** Absolute base URL of the API, e.g. https://api.jotty.id (no trailing slash) */
  baseUrl: string;
  /** Override window.fetch — useful for tests / SSR. Defaults to globalThis.fetch. */
  fetch?: typeof fetch;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? deriveMessage(status, body));
    this.status = status;
    this.body = body;
    this.name = "ApiError";
  }
}

function deriveMessage(status: number, body: unknown): string {
  if (body && typeof body === "object" && "error" in body && typeof (body as { error: unknown }).error === "string") {
    return (body as { error: string }).error;
  }
  return `API request failed with ${status}`;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly _fetch: typeof fetch;
  private csrfToken: string | null = null;
  private csrfPromise: Promise<void> | null = null;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, "");
    this._fetch = config.fetch ?? globalThis.fetch.bind(globalThis);
  }

  /**
   * Calls /auth/csrf once, caches the token, and stores the cookie. Subsequent
   * requests are no-ops. Must complete before any mutating call — request()
   * triggers it lazily.
   */
  async initCsrf(): Promise<void> {
    if (this.csrfToken) return;
    if (this.csrfPromise) return this.csrfPromise;
    this.csrfPromise = (async () => {
      const res = await this._fetch(this.baseUrl + "/api/v1/auth/csrf", {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) throw new ApiError(res.status, null, "csrf init failed");
      const body = (await res.json()) as { csrf_token: string };
      this.csrfToken = body.csrf_token;
    })();
    try {
      await this.csrfPromise;
    } finally {
      this.csrfPromise = null;
    }
  }

  /** Generic typed fetch wrapper. Handles JSON encode/decode + CSRF + cookies. */
  async request<T>(path: string, init: RequestInit & { mutating?: boolean } = {}): Promise<T> {
    const { mutating = init.method !== undefined && init.method !== "GET" && init.method !== "HEAD", ...rest } = init;
    if (mutating) await this.initCsrf();

    const headers = new Headers(rest.headers);
    if (rest.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
    if (mutating && this.csrfToken) headers.set("X-CSRF-Token", this.csrfToken);
    headers.set("Accept", "application/json");

    const res = await this._fetch(this.baseUrl + path, {
      ...rest,
      headers,
      credentials: "include",
    });

    if (!res.ok) {
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // ignore
      }
      throw new ApiError(res.status, body);
    }
    if (res.status === 204) return undefined as T;
    if (res.headers.get("Content-Length") === "0") return undefined as T;
    return (await res.json()) as T;
  }

  // Convenience wrappers — keeps call sites tight.
  get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: "GET" });
  }
  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined });
  }
  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined });
  }
  delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: "DELETE" });
  }
}
